import { sharedValidators } from "./shared";

const flightValidators = {
  // =====================================================
  // STEP 1 → Destination selection
  // =====================================================
  1: ({ step1 }, t) => {
    if (!step1?.airline_code?.trim()) return t("please_select_airline");

    for (let i = 0; i < step1.destinations.length; i++) {
      const d = step1.destinations[i];
      const row = i + 1;

      if (!d.departure_country_id?.trim())
        return t("please_select_origin_for_row", { row });

      if (!d.arrival_country_id?.trim())
        return t("please_select_destination_for_row", { row });
    }

    return null;
  },

  // =====================================================
  // STEP 2 → Location (shared logic)
  // =====================================================
  2: ({ step1 }, t) => {
    if (!step1.country_id) return t("please_select_country");
    if (!step1.city_id) return t("please_select_city");
    if (!step1.address) return t("please_select_address");

    return null;
  },

  // =====================================================
  // STEP 3 → Type
  // =====================================================
  3: ({ step1 }, t) => {
    if (!step1.flight_type_id) return t("please_select_flight_type");
    return null;
  },

  // =====================================================
  // STEP 4 → Flights (passengers + itinerary count)
  // =====================================================
  4: ({ step2 }, t) => {
    if (!step2.flight || step2.flight.length === 0)
      return t("please_add_flight_details");

    for (let fIndex = 0; fIndex < step2.flight.length; fIndex++) {
      const f = step2.flight[fIndex];
      const flightNum = fIndex + 1; // flight number for translations

      const ctx = { flight: flightNum };

      if (!f.origin_code?.trim()) return t("please_enter_origin_airport", ctx);

      if (!f.destination_code?.trim())
        return t("please_enter_destination_airport", ctx);

      if (!f.trip?.trim()) return t("please_enter_trip_type", ctx);

      if (f.itinerary_count <= 0) return t("please_enter_itinerary_count", ctx);

      if (f.adult <= 0) return t("please_enter_adult_count", ctx);

      if (f.child < 0) return t("please_enter_child_count", ctx);

      if (f.infant < 0) return t("please_enter_infant_count", ctx);

      if (f.luggage_cout < 0 || !f.luggage_cout.trim()) return t("please_enter_luggage_count", ctx);

      if (!f.luggage_type?.trim()) return t("please_enter_luggage_type", ctx);
    }

    return null;
  },

  // =====================================================
  // STEP 5 → Itineraries + segments
  // =====================================================
  // =====================================================
  // STEP 5 → Itineraries + Segments
  // =====================================================
  5: ({ step3 }, t) => {
    if (!Array.isArray(step3.flight) || step3.flight.length === 0)
      return t("please_add_flight_details");

    for (let fIndex = 0; fIndex < step3.flight.length; fIndex++) {
      const f = step3.flight[fIndex];
      const flightNum = fIndex + 1; // Always specify flight number
      const segments = f.itineraries?.segments ?? [];

      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];
        const segNum = i + 1;

        const ctx = { seg: segNum, flight: flightNum };

        // ──────────────────────────────
        // Basic field validation
        // ──────────────────────────────
        if (!seg.flight_number?.trim() )
          return t("please_enter_flight_number", ctx);
        if (seg.flight_number?.length < 5)
          return t("flight_number_invalid", ctx);

        if (!seg.airline_code?.trim())
          return t("please_select_airline_itinerary", ctx);

        if (!seg.departure_airport_code?.trim())
          return t("please_select_origin_segment", ctx);

        if (!seg.arrival_airport_code?.trim())
          return t("please_select_destination_segment", ctx);

        if (!seg.departure_at?.trim())
          return t("please_select_departure_time", ctx);

        if (!seg.departure_terminal?.trim())
          return t("please_select_departure_terminal", ctx);

        if (!seg.arrival_at?.trim())
          return t("please_select_arrival_time", ctx);

        if (!seg.arrival_terminal?.trim())
          return t("please_select_arrival_terminal", ctx);

        // ──────────────────────────────
        // Airports must differ
        // ──────────────────────────────
        if (seg.departure_airport_code === seg.arrival_airport_code)
          return t("origin_and_destination_must_differ", ctx);

        // ──────────────────────────────
        // Date validation
        // ──────────────────────────────
        const departure = new Date(seg.departure_at);
        const arrival = new Date(seg.arrival_at);

        if (isNaN(departure.getTime()) || isNaN(arrival.getTime()))
          return t("invalid_datetime_format", ctx);

        if (departure >= arrival)
          return t("arrival_must_be_after_departure", ctx);

        // ──────────────────────────────
        // Max duration = 20 hours
        // ──────────────────────────────
        const hours = (arrival - departure) / 36e5;

        if (hours > 20)
          return t("duration_exceeds_limit", {
            ...ctx,
            max: 20,
          });
      }
    }

    return null;
  },

  // =====================================================
  // STEP 6 → Pricing
  // =====================================================
  6: ({ step4 }, t) => {
    for (let fIndex = 0; fIndex < step4.flight.length; fIndex++) {
      const f = step4.flight[fIndex];
      const flightNum = fIndex + 1; // flight number for translation context
      const ctx = { flight: flightNum };

      if (f.pricing.adult <= 0) return t("please_enter_valid_adult_price", ctx);

      if (f.pricing.child < 0) return t("please_enter_valid_child_price", ctx);

      if (f.pricing.infant < 0)
        return t("please_enter_valid_infant_price", ctx);
    }

    return null;
  },

  // =====================================================
  // STEP 7 → Policy
  // =====================================================
  7: ({ step4 }, t) => {
    if (!step4.cancellation_policy_id)
      return t("please_select_cancellation_policy");

    return null;
  },

  8: ({ step4 }, t) => {
    if (!step4.booking_confirmation_mode)
      return t("please_select_booking_confirmation");

    return null;
  },
};

export default flightValidators;
